import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import styles from './HATVNotes.module.scss';
import { countryCodeMappings, NOTES_SORT_LIST } from '../../../constants/dataConstants';
import sortIconLight from '../../../assets/images/LightTheme/up_down.svg';
import sortIconDark from '../../../assets/images/DarkTheme/up_down_dark.svg';
import { map } from 'lodash';
import FormatterBar from '../../common/FormatterBar/FormatterBar';
import Button from '../../common/Button/Button';
import { getFullNameOfUserByNativeID } from '../../../utils/progressNotesUtil';
import { NestedObject, UserDetails } from '../../../types/types';
import { formatDate } from '../../../utils/dateFormatter';
import HTMLtoText from '../../common/HTMLToText/HTMLToText';
import {
  convertUrlsToLinks,
  formatDatetoLong,
  isOwner,
  translateKey,
  useProfileParams,
} from '../../../utils/common';
import { RootState } from '../../../redux/store';
import { useGetUsersListByNativeIDsQuery } from '../../../redux/services/progressNotesAPI';
import isEmpty from 'lodash/isEmpty';
import {
  // useGetHatvNotesListQuery,
  useCreateHatvNotesMutation,
  useEditHatvNotesMutation,
  useDeleteHatvNotesMutation,
} from '../../../redux/services/hatvQMonthNotesAPI';
import { getUserNativeIDsArray } from '../../../utils/progressNotesUtil';
import { isValidResponse } from '../../../utils/validateAPIResponse';
import useClickOutside from '../../CustomHooks/useClickOutside';
import ABOBackgroundLoader from '../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader';
import ErrorInPageScreen from '../../common/ErrorInPageScreen/ErrorInPageScreen';
import { useMonthOptions } from '../../Context/MonthYearOptionsContext';
import FlatButtonAction from '../../common/FlatButtonAction/FlatButtonAction';
import { getYearFromQMonth } from '../../../utils/common';
import GenericIcon from '../../common/GenericIcon/GenericIcon';

interface HatvNotes {
  metadata?: any;
  aboNoteId: number;
  note: string;
  title: string;
  performanceYear: number;
  audit: {
    createdDate: string;
    createdBy: string;
    updatedDate: string;
    updatedBy: string;
  };
}

type FilterDataType = {
  id: string;
  name: string;
};

interface HATVNotesProps {
  type: string;
  selectedTabData?: NestedObject;
  isStatusUpdate?: string | null;
  hatvNotesAPI: {
    useGetHatvNotes: Function;
  };
}

const HATVNotes: React.FC<HATVNotesProps> = ({
  type,
  selectedTabData,
  isStatusUpdate,
  hatvNotesAPI,
}) => {
  const { t } = useTranslation();
  const { useGetHatvNotes } = hatvNotesAPI;

  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [hatvNotes, setHatvNotes] = useState<HatvNotes[]>([]);
  const [textareaValue, setTextareaValue] = useState<string>('');
  const [isEditable, setIsEditable] = useState(false);
  const [saveEnable, setSaveEnable] = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);
  const [sortByNewest, setSortByNewest] = useState(true);
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const [isAPIError, setIsAPIError] = useState<boolean>(false);
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [selectedSort, setSelectedSort] = useState<FilterDataType>(NOTES_SORT_LIST?.[0]);
  const [isNoteEditable, setNoteEditable] = useState(false);
  const [usersListByNativeId, setUsersListByNativeId] = useState<UserDetails[] | null>([]);
  const [updatedNativeIDs, setUpdatedNativeIDs] = useState<string[]>([]);
  const [expandedNotes, setExpandedNotes] = useState<string[]>([]);
  const [expandedNoteIds, setExpandedNoteIds] = useState<any[]>([]);
  const [userNativeIDs, setUserNativeIDs] = useState<string[]>([]);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const sortIcon = currentTheme === 'light' ? sortIconLight : sortIconDark;
  const { selectedPYYear } = useMonthOptions();

  const { selectedYearMonth } = useMonthOptions();
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpenPopup(false));

  const handleClickSortIcon = () => {
    setIsOpenPopup(!isOpenPopup);
  };
  const selectedYear = selectedYearMonth ? getYearFromQMonth(selectedYearMonth) : '';

  const { affiliateCode, aboNumber } = useProfileParams();

  //API call to get list of all notes
  const {
    isFetching: isNotesLoading,
    isError: isErrorInFetchNotes,
    data: notesData,
    refetch: isNotesRefeching,
    isUninitialized,
  } = useGetHatvNotes(
    type === 'qapproval'
      ? {
          affiliateCode,
          aboNumber,
          year: selectedYear,
          type,
        }
      : {
          affiliateCode,
          aboNumber,
          year: selectedYear,
          awardCode: Number(selectedTabData?.trackingAwardCode),
          qualifiedMonth: selectedTabData?.awardQualifiedMonth,
          type,
        },
    {
      skip:
        isAPIError ||
        !selectedYear ||
        (type === 'awardapproval' &&
          (!selectedTabData?.trackingAwardCode || !selectedTabData?.awardQualifiedMonth)),
    }
  );

  useEffect(() => {
    if (isStatusUpdate !== null && !isUninitialized) {
      isNotesRefeching();
    }
  }, [isStatusUpdate]);

  useEffect(() => {
    if (
      type === 'awardapproval' &&
      selectedTabData?.trackingAwardCode &&
      selectedTabData?.awardQualifiedMonth
    ) {
      // refetch notes when navigating from HATV Dashboard to this page when selectedTabData is ready
      isNotesRefeching();
    }
  }, [selectedTabData, type]);

  // Get the list of user details
  const { data: usersList } = useGetUsersListByNativeIDsQuery(
    {
      nativeIDs: userNativeIDs,
    },
    {
      skip: isEmpty(userNativeIDs) || userNativeIDs === updatedNativeIDs,
    }
  );
  useEffect(() => {
    setUsersListByNativeId(usersList?.body);
    if (!isEmpty(usersList)) {
      setUpdatedNativeIDs(userNativeIDs);
    }
  }, [usersList]);

  const sortDataByOrder = (sortByNewest: boolean) => {
    if (sortByNewest) {
      return [...notesData.body]
        .filter((note) => note.audit && note.audit.updatedDate)
        .sort(
          (a, b) =>
            new Date(b.audit.updatedDate).getTime() - new Date(a.audit.updatedDate).getTime()
        );
    } else {
      return [...notesData.body]
        .filter((note) => note.audit && note.audit.updatedDate)
        .sort(
          (a, b) =>
            new Date(a.audit.updatedDate).getTime() - new Date(b.audit.updatedDate).getTime()
        );
    }
  };

  const handleChangeSort = (sortItem: FilterDataType) => {
    if (sortItem?.id !== selectedSort?.id) {
      const sortedNotes = [...hatvNotes].reverse();
      setHatvNotes(sortedNotes);
    }
    setSelectedSort(sortItem);
  };

  const handleAPIError = () => {
    setIsAPIError(true);
    setHatvNotes([]);
  };

  useEffect(() => {
    if (!notesData) return;

    if (!isValidResponse(notesData?.statusCode)) {
      handleAPIError();
      return;
    }

    if (!Array.isArray(notesData.body)) {
      setHatvNotes([]);
      return;
    }

    const sortedNotes = sortDataByOrder(selectedSort?.id === 'newest_first');
    setHatvNotes(sortedNotes);
    setUserNativeIDs(getUserNativeIDsArray(sortedNotes));
  }, [notesData, isNotesLoading]);

  const textAreaChangeHandler = (editorData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorData, 'text/html');
    const rowNote = (doc.body.textContent || '').trim();
    const hasImage = !!doc.querySelector('img');
    setSaveEnable(hasImage || rowNote.length > 0);
    editorData === '<p><br></p>' ? setTextareaValue('') : setTextareaValue(editorData);
  };

  const handleClickCancel = () => {
    setIsEditable(false);
    setNoteEditable(false);
  };

  const focusHandler = () => {
    setIsEditable(true);
    setSaveEnable(false);
    setNoteEditable(false);
  };

  // API call to delete a hatv note
  const [deleteHatvNote, { isLoading: isLoadingDelete, isError: isErrorInDelete }] =
    useDeleteHatvNotesMutation();

  const handleDeleteNote = async (noteId: number, type: string) => {
    const response: any = await deleteHatvNote({
      id: noteId,
      type,
    });

    if (!isValidResponse(response?.data?.statusCode)) {
      handleAPIError();
    }
  };

  const handleEditTag = (updatedDate: string, createdDate: string) => {
    if (new Date(updatedDate)?.toLocaleString() === new Date(createdDate)?.toLocaleString()) {
      return '';
    } else {
      return t('edited');
    }
  };
  const handleEdit = (note: any) => {
    setSaveEnable(false);
    setNoteEditable(true);
    setIsEditable(false);
    setTextareaValue(note.note);
    setEditNoteId(note.aboNoteId);
  };

  // API call to create new hatv note.
  const [createNotes, { isLoading: isNotesCreating, isError: isErrorInCreateNote }] =
    useCreateHatvNotesMutation();

  // API call to Edit hatv notes.
  const [editNotes, { isLoading: isNotesEditing, isError: isErrorInEditNote }] =
    useEditHatvNotesMutation();

  useEffect(() => {
    if (isErrorInFetchNotes || isErrorInCreateNote || isErrorInEditNote || isErrorInDelete)
      handleAPIError();
  }, [isErrorInFetchNotes, isErrorInCreateNote, isErrorInEditNote, isErrorInDelete]);

  const handleClickSave = async () => {
    const payload = {
      performanceYear: selectedYear,
      title: '',
      note: textareaValue,
      ...(type === 'qapproval' && {
        metadata: {
          closingMonth: selectedYearMonth || '',
        },
      }),
      ...(type === 'awardapproval' &&
        selectedTabData && {
          metadata: {
            awardQualifiedMonth: selectedTabData?.awardQualifiedMonth,
            awardCode: selectedTabData?.trackingAwardCode,
          },
        }),
    };

    if (editNoteId) {
      const editNotesResponse: any = await editNotes({
        affiliateCode,
        aboNumber,
        payload: { ...payload, aboNoteId: editNoteId },
        type,
      });
      setEditNoteId(null);
      if (!isValidResponse(editNotesResponse?.data?.statusCode)) {
        handleAPIError();
      }
    } else {
      const response: any = await createNotes({
        affiliateCode,
        aboNumber,
        payload: payload,
        type,
      });

      if (!isValidResponse(response?.data?.statusCode)) {
        handleAPIError();
      }
    }

    setIsEditable(false);
    setNoteEditable(false);
  };
  const handleReadMoreToggle = (noteId: any) => {
    setExpandedNoteIds((prev) =>
      prev.includes(noteId) ? prev.filter((id) => id !== noteId) : [...prev, noteId]
    );
  };

  if (type === 'awardapproval' && !selectedTabData) {
    return null;
  }

  return (
    <section className={`hatv_notes_container ${styles.hatv_notes_container}`}>
      {isNotesCreating || isNotesLoading || isNotesEditing || isLoadingDelete ? (
        <ABOBackgroundLoader num={3} heading={t('notes')} />
      ) : isAPIError ? (
        <>
          <div className={styles.heading_area}>
            <div className={styles.title} data-testid="hatv_notes_bg_heading_testID">
              {t('notes')}
            </div>
          </div>
          <div data-testid="error_testId">
            <ErrorInPageScreen
              handleClickTryAgain={() => {
                setIsAPIError(false);
              }}
            />
          </div>
        </>
      ) : (
        <>
          <div className={styles.heading_area}>
            <div className={styles.title} data-testid="hatv_notes_bg_heading_testID">
              {t('notes')}
            </div>

            <div className={styles.sort_section} onClick={handleClickSortIcon}>
              <div className={styles.sort_label}>
                {selectedSort?.id === 'newest_first' ? t('newest_first') : t('oldest_first')}
              </div>
              <img src={sortIcon} alt="" className={styles.sort_icon} />

              {isOpenPopup && (
                <div className={`dropdown_container ${styles.dropdown_container}`}>
                  <div ref={dropdownRef} className={`dropdown_menu ${styles.dropdown_menu}`}>
                    {map(NOTES_SORT_LIST, (filterItem) => (
                      <div
                        key={filterItem.id}
                        className={`${selectedSort?.id === filterItem.id ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                        onClick={() => handleChangeSort(filterItem)}
                      >
                        {filterItem.id ? t(`${filterItem.id}`) : ''}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.input_wrapper}>
            {isEditable ? (
              <>
                <div className={styles.formatter_wrapper}>
                  <FormatterBar
                    id="Hatvnotes"
                    testId="hatv_notes_testID"
                    editorData={''}
                    handleDataChange={textAreaChangeHandler}
                    section="notes"
                    placeholder={t('add_internal_hatvnotes')}
                    isContentEditable={true}
                    maxCharacter={20000}
                  />
                </div>

                <div className={styles.btn_wrapper}>
                  <Button
                    id="cancelButton"
                    onClick={handleClickCancel}
                    label={t('btnCancel')}
                    style="unfilled"
                  />
                  <Button
                    id="saveButton"
                    onClick={handleClickSave}
                    label={t('btnSave')}
                    style="filled"
                    isDisabled={!textareaValue || !saveEnable}
                  />
                </div>
              </>
            ) : (
              <input
                type="text"
                className={`input_text_area ${styles.input_text_area}`}
                placeholder={t('add_internal_hatvnotes')}
                value={t('add_internal_hatvnotes')}
                onClick={focusHandler}
                readOnly
                data-testid="textarea_testID"
              />
            )}
          </div>

          <div className={styles.notes_list}>
            {map(showAllNotes ? hatvNotes : hatvNotes.slice(0, 3), (note) => (
              <div key={note.aboNoteId} className={`hatv_note ${styles.hatv_note}`}>
                <div className={`left_wrapper ${styles.left_wrapper}`}>
                  <GenericIcon isLight={currentTheme === 'light'} />
                </div>

                <div className={`right_wrapper ${styles.right_wrapper}`}>
                  <div className={styles.user_info}>
                    <div className={styles.user_name}>
                      {getFullNameOfUserByNativeID(note.audit.createdBy, usersListByNativeId)}
                    </div>
                    <div className={`last_modified ${styles.last_modified}`}>
                      {formatDate(
                        note.audit.updatedDate,
                        countryCodeMappings[userAffiliateCode],
                        'dateTime'
                      )}
                      {handleEditTag(note.audit.updatedDate, note.audit.createdDate) && (
                        <span className={styles.edited_tag}>
                          {handleEditTag(note.audit.updatedDate, note.audit.createdDate)}
                        </span>
                      )}
                      {/* {"  "}| Achieved F. Platinum */}
                      <span>&nbsp;</span>
                      <span>{` |`}&nbsp;&nbsp;</span>
                      {type === 'qapproval' &&
                        `${t(translateKey('hatv_violationPeriod', type))}: ${formatDatetoLong(`${note.metadata?.closingMonth}`)}`}
                    </div>
                  </div>

                  <div className={styles.input_wrapper}>
                    {isNoteEditable && editNoteId === note.aboNoteId ? (
                      <>
                        <div className={styles.formatter_wrapper} data-testid="editor_testID">
                          <FormatterBar
                            id="Hatvnotes"
                            testId="hatv_notes_testID"
                            editorData={textareaValue}
                            handleDataChange={textAreaChangeHandler}
                            section="notes"
                            placeholder={t('add_internal_hatvnotes')}
                            isContentEditable={true}
                            maxCharacter={20000}
                          />
                        </div>

                        <div className={styles.btn_wrapper}>
                          <Button
                            id="cancelButton"
                            onClick={handleClickCancel}
                            label={t('btnCancel')}
                            style="unfilled"
                          />
                          <Button
                            id="saveButton"
                            onClick={handleClickSave}
                            label={t('btnSave')}
                            style="filled"
                            isDisabled={!textareaValue || !saveEnable}
                          />
                        </div>
                      </>
                    ) : (
                      <div>
                        <div
                          className={`note_content ${styles.note_content}`}
                          data-testid="all_notes_testID"
                        >
                          <p>
                            <span className={styles.input_note}>
                              <HTMLtoText
                                htmlContent={convertUrlsToLinks(note?.note, currentTheme)}
                                maxTextLength={200}
                              />
                            </span>
                          </p>
                        </div>
                        <div className={`action_btns ${styles.action_btns}`}>
                          <FlatButtonAction
                            label={t('edit')}
                            onClick={() => handleEdit(note)}
                            className={styles.action_edit}
                            testId="edit_testID"
                            disabled={!isOwner(note?.audit?.createdBy)}
                            tooltipDescription={'access_restricted_to_creator'}
                          />
                          <FlatButtonAction
                            label={t('delete')}
                            onClick={() => handleDeleteNote(note.aboNoteId, type)}
                            className={styles.action_delete}
                            testId="delete_testID"
                            disabled={!isOwner(note?.audit?.createdBy)}
                            tooltipDescription={'access_restricted_to_creator'}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {hatvNotes.length > 3 && (
        <div
          className={`view_more_notes ${styles.view_more_notes}`}
          onClick={() => setShowAllNotes((prev) => !prev)}
        >
          {showAllNotes ? t('view_less') : t('view_more')}
        </div>
      )}
    </section>
  );
};

export default HATVNotes;
